import React, { useState } from 'react';
import { Row, Col, Card, Typography, Tag, Empty, Image } from 'antd';
import { PictureOutlined, ToolOutlined } from '@ant-design/icons';
import { shopConfig, getThemeColors } from '../../config/shop.config';

const { Title, Text, Paragraph } = Typography;

const GallerySection: React.FC = () => {
  const themeColors = getThemeColors();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const gallery = shopConfig.gallery || { enabled: true, images: [], categories: [] };

  if (!gallery.enabled || gallery.images.length === 0) {
    return null; // Don't show section if disabled or no images
  }

  const categories = ['All', ...gallery.categories];
  const filteredImages =
    selectedCategory === 'All'
      ? gallery.images
      : gallery.images.filter((img) => img.category === selectedCategory);

  return (
    <div id="gallery" style={{ padding: '80px 20px', backgroundColor: '#f9fafb' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <Title level={2} style={{ color: themeColors.primary }}>
            <ToolOutlined style={{ marginRight: '12px' }} />
            Our Work
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#6b7280', maxWidth: '700px', margin: '0 auto' }}>
            See examples of the quality workmanship and expertise we bring to every job
          </Paragraph>
        </div>

        {/* Category Filters */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'inline-flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {categories.map((category) => (
              <Tag
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '8px 20px',
                  fontSize: '15px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: `2px solid ${selectedCategory === category ? themeColors.primary : '#e5e7eb'}`,
                  backgroundColor: selectedCategory === category ? themeColors.primary : 'white',
                  color: selectedCategory === category ? 'white' : '#111827',
                  fontWeight: selectedCategory === category ? 600 : 500,
                  transition: 'all 0.3s ease',
                }}
              >
                {category}
              </Tag>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        {filteredImages.length > 0 ? (
          <Row gutter={[24, 24]}>
            {filteredImages.map((item) => {
              const isVideo = item.type === 'video';
              const videoId = isVideo ? item.url : '';
              const embedUrl = isVideo
                ? item.videoProvider === 'vimeo'
                  ? `https://player.vimeo.com/video/${videoId}`
                  : `https://www.youtube.com/embed/${videoId}`
                : '';

              return (
                <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                  <Card
                    hoverable
                    cover={
                      <div
                        style={{
                          height: '200px',
                          overflow: 'hidden',
                          backgroundColor: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {isVideo ? (
                          <iframe
                            src={embedUrl}
                            title={item.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <Image
                            src={item.url}
                            alt={item.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            preview={{
                              mask: <div style={{ fontSize: '14px' }}>Click to enlarge</div>,
                            }}
                          />
                        )}
                      </div>
                    }
                    style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                    }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '15px' }}>
                      {item.title}
                    </Text>
                    {item.description && (
                      <Text type="secondary" style={{ fontSize: '13px', display: 'block' }}>
                        {item.description}
                      </Text>
                    )}
                    <Tag
                      color={themeColors.primary}
                      style={{ marginTop: '8px', borderRadius: '4px', fontSize: '12px' }}
                    >
                      {item.category}
                    </Tag>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Empty
            description="No images in this category"
            style={{ padding: '60px 0' }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {/* Call to Action */}
        <div
          style={{
            marginTop: '60px',
            textAlign: 'center',
            padding: '40px',
            background: `linear-gradient(135deg, ${themeColors.primary}15 0%, ${themeColors.secondary}15 100%)`,
            borderRadius: '16px',
            border: `2px solid ${themeColors.primary}30`,
          }}
        >
          <Title level={3} style={{ color: themeColors.primary, marginBottom: '12px' }}>
            Ready to Experience Our Quality Work?
          </Title>
          <Text style={{ fontSize: '16px', color: '#4b5563' }}>
            Contact us today to discuss your automotive needs and get a free quote
          </Text>
        </div>
      </div>
    </div>
  );
};

export default GallerySection;
